/**
 * @voltxampere/presets — Simple resistor pair (Sprint 50-2)
 *
 * Two resistors in series — total = R1 + R2. The simplest possible
 * circuit beyond a single component, used as the literal "first
 * step" in the Marmara Mekatronik beginner kit. With V1=10V and
 * R1=R2=1kΩ, the current is I = 10/(1k+1k) = 5 mA and the midpoint
 * voltage equals 5V (same as voltage-divider — pedagogical sibling).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const simpleResistorPair = {
    id: "simple-resistor-pair",
    name: { tr: "Seri İki Direnç", en: "Two Resistors in Series" },
    category: "basic",
    difficulty: 1,
    description: {
        tr: "İki direnci seri bağlayın. Toplam direnç = R1+R2. V1=10V, R1=R2=1kΩ → I=5mA.",
        en: "Two resistors connected in series. Total = R1+R2. V1=10V, R1=R2=1kΩ → I=5mA.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Simple Resistor Pair",
        description: "Two resistors in series",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "mid" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 10 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_vcc", p2: "net_mid" },
                label: "R1",
                position: { x: 0, y: -60 },
                rotation: 90,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_mid", p2: "net_gnd" },
                label: "R2",
                position: { x: 0, y: 60 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            {
                id: "w_vcc",
                netId: "net_vcc",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -80 },
                    { x: 0, y: -80 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_mid",
                netId: "net_mid",
                path: [
                    { x: 0, y: -40 },
                    { x: 0, y: 40 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 0, y: 80 },
                    { x: 0, y: 110 },
                    { x: -160, y: 110 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "mid",
            analysis: "dc",
            value: 5,
            tolerance: 0.001,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Seri devrede toplam direnç R_toplam = R1 + R2.",
            en: "Series total resistance R_total = R1 + R2.",
        },
        {
            tr: "Aynı akım her seri elemandan geçer (KCL).",
            en: "The same current flows through each series element (KCL).",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 1"],
};
//# sourceMappingURL=simple-resistor-pair.js.map